import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { DollarSignIcon, SearchIcon, FilterIcon, ChevronDownIcon, RefreshCwIcon, DownloadIcon, EyeIcon, MoreHorizontalIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ArrowUpIcon, ArrowDownIcon, CalendarIcon, ClockIcon, TagIcon, FileTextIcon, UserIcon, BuildingIcon, BarChart2Icon, InfoIcon, HelpCircleIcon, CreditCardIcon, ArrowRightIcon, PrinterIcon, ExternalLinkIcon, ChevronRightIcon, ChevronLeftIcon, MailIcon, PhoneIcon, ClipboardIcon, ShieldIcon, TrendingUpIcon, ReceiptIcon, SmartphoneIcon, WalletIcon, PieChartIcon, LayersIcon, ArrowLeftIcon, XIcon } from 'lucide-react';
const PaymentManagement = () => {
  const {
    admin,
    isSuperAdmin,
    getMdaName
  } = useAdminAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<any[]>([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentStats, setPaymentStats] = useState({
    totalAmount: 0,
    totalPayments: 0,
    successfulPayments: 0,
    pendingPayments: 0,
    failedPayments: 0,
    averageAmount: 0
  });
  // Mock data generation
  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        const mockPayments = generateMockPayments(100);
        setPayments(mockPayments);
        setFilteredPayments(mockPayments);
        setTotalItems(mockPayments.length);
        // Calculate payment stats
        const totalAmount = mockPayments.reduce((sum, payment) => sum + payment.amount, 0);
        const successfulPayments = mockPayments.filter(p => p.status === 'successful').length;
        const pendingPayments = mockPayments.filter(p => p.status === 'pending').length;
        const failedPayments = mockPayments.filter(p => p.status === 'failed').length;
        setPaymentStats({
          totalAmount,
          totalPayments: mockPayments.length,
          successfulPayments,
          pendingPayments,
          failedPayments,
          averageAmount: totalAmount / mockPayments.length
        });
        setIsLoading(false);
      }, 1000);
    };
    fetchPayments();
  }, []);
  // Generate mock payment data
  const generateMockPayments = (count: number) => {
    const statuses = ['successful', 'pending', 'failed'];
    const services = ['Business Registration', 'Tax Clearance Certificate', 'Land Title Registration', 'Vehicle Registration', 'Building Permit', 'Market Stall Fee', 'Environmental Compliance', 'Marriage Certificate', 'Birth Certificate', 'Death Certificate'];
    const mdas = ['Ministry of Lands', 'Ministry of Finance', 'Ministry of Transportation', 'Ministry of Health', 'Ministry of Education', 'Internal Revenue Service', 'Civil Registry', 'Environmental Protection Agency', 'Urban Development Board', 'Commerce and Industry'];
    const paymentMethods = ['card', 'bank transfer', 'ussd', 'wallet', 'cash'];
    const users = [{
      name: 'John Okafor',
      email: 'john.o@example.com',
      phone: '+234 801 234 5678'
    }, {
      name: 'Amina Ibrahim',
      email: 'amina.i@example.com',
      phone: '+234 802 345 6789'
    }, {
      name: 'Chidi Okonkwo',
      email: 'chidi.o@example.com',
      phone: '+234 803 456 7890'
    }, {
      name: 'Fatima Bello',
      email: 'fatima.b@example.com',
      phone: '+234 804 567 8901'
    }, {
      name: 'Emmanuel Adebayo',
      email: 'emmanuel.a@example.com',
      phone: '+234 805 678 9012'
    }, {
      name: 'Ngozi Eze',
      email: 'ngozi.e@example.com',
      phone: '+234 806 789 0123'
    }, {
      name: 'Ibrahim Musa',
      email: 'ibrahim.m@example.com',
      phone: '+234 807 890 1234'
    }, {
      name: 'Blessing Obi',
      email: 'blessing.o@example.com',
      phone: '+234 808 901 2345'
    }, {
      name: 'Yusuf Ahmed',
      email: 'yusuf.a@example.com',
      phone: '+234 809 012 3456'
    }, {
      name: 'Grace Adeyemi',
      email: 'grace.a@example.com',
      phone: '+234 810 123 4567'
    }];
    return Array.from({
      length: count
    }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 60)); // Random date within last 60 days
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const service = services[Math.floor(Math.random() * services.length)];
      const mda = mdas[Math.floor(Math.random() * mdas.length)];
      const paymentMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
      const user = users[Math.floor(Math.random() * users.length)];
      const amount = Math.floor(Math.random() * 50000) + 1000; // Random amount between 1,000 and 50,000
      return {
        id: `PAY-${date.getFullYear()}-${(i + 1).toString().padStart(5, '0')}`,
        referenceNumber: `REF${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
        amount,
        serviceName: service,
        serviceId: `SRV-${Math.floor(Math.random() * 1000) + 1}`,
        mdaName: mda,
        mdaId: `MDA-${Math.floor(Math.random() * 100) + 1}`,
        userName: user.name,
        userEmail: user.email,
        userPhone: user.phone,
        userId: `USR-${Math.floor(Math.random() * 1000) + 1}`,
        date: date.toISOString(),
        status,
        paymentMethod,
        transactionId: `TRX${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
        applicationId: `APP-${Math.floor(Math.random() * 1000) + 1}`,
        description: `Payment for ${service}`,
        processingFee: Math.floor(Math.random() * 500) + 100
      };
    });
  };
  // Apply filters and search
  useEffect(() => {
    let result = [...payments];
    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(payment => payment.status === filterStatus);
    }
    // Apply date filter
    if (filterDate !== 'all') {
      const today = new Date();
      const filterDate = new Date();
      switch (filterDate) {
        case 'today':
          result = result.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate.toDateString() === today.toDateString();
          });
          break;
        case 'week':
          filterDate.setDate(today.getDate() - 7);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate >= filterDate;
          });
          break;
        case 'month':
          filterDate.setMonth(today.getMonth() - 1);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate >= filterDate;
          });
          break;
        case 'quarter':
          filterDate.setMonth(today.getMonth() - 3);
          result = result.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate >= filterDate;
          });
          break;
      }
    }
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(payment => payment.id.toLowerCase().includes(query) || payment.referenceNumber.toLowerCase().includes(query) || payment.userName.toLowerCase().includes(query) || payment.serviceName.toLowerCase().includes(query) || payment.mdaName.toLowerCase().includes(query) || payment.transactionId.toLowerCase().includes(query));
    }
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'service':
          comparison = a.serviceName.localeCompare(b.serviceName);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    setFilteredPayments(result);
    setTotalItems(result.length);
    setCurrentPage(1);
  }, [payments, filterStatus, filterDate, searchQuery, sortField, sortDirection]);
  // Get current page items
  const getCurrentItems = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  };
  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate API call delay
    setTimeout(() => {
      const mockPayments = generateMockPayments(100);
      setPayments(mockPayments);
      // Show success message
      setSuccessMessage('Payment data refreshed successfully');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      setIsRefreshing(false);
    }, 1500);
  };
  // Handle export
  const handleExport = (format: string) => {
    setSuccessMessage(`Exporting payment data as ${format.toUpperCase()}...`);
    setShowSuccessMessage(true);
    // Simulate export delay
    setTimeout(() => {
      setSuccessMessage(`Payment data exported as ${format.toUpperCase()} successfully`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }, 1500);
  };
  // Handle view receipt
  const handleViewReceipt = (payment: any) => {
    setSelectedPayment(payment);
    setShowReceiptModal(true);
  };
  // Handle print receipt
  const handlePrintReceipt = () => {
    setSuccessMessage('Printing receipt...');
    setShowSuccessMessage(true);
    // Simulate print delay
    setTimeout(() => {
      setSuccessMessage('Receipt sent to printer');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }, 1500);
  };
  // Handle download receipt
  const handleDownloadReceipt = () => {
    setSuccessMessage('Preparing receipt for download...');
    setShowSuccessMessage(true);
    // Simulate download delay
    setTimeout(() => {
      setSuccessMessage('Receipt downloaded successfully');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }, 1500);
  };
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is applied in the useEffect
  };
  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-NG', options);
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG')}`;
  };
  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'successful':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'successful':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-2673" />;
      case 'pending':
        return <ClockIcon size={16} className="text-yellow-500" data-id="element-2674" />;
      case 'failed':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-2675" />;
      default:
        return <InfoIcon size={16} className="text-gray-500" data-id="element-2676" />;
    }
  };
  // Set initial sidebar state based on screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    // Set initial state
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-2677">
        <div className="flex flex-col items-center" data-id="element-2678">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-2679"></div>
          <p className="text-gray-600" data-id="element-2680">Loading payment data...</p>
          <p className="text-xs text-gray-500 mt-2" data-id="element-2681">
            Please wait while we fetch the latest information
          </p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-2682">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-2683">
        <AdminSidebar data-id="element-2684" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-2685"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2686">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-2687" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-gray-50" data-id="element-2688">
          {/* Page Header */}
          <div className="mb-6" data-id="element-2689">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-2690">
              <div className="mb-4 md:mb-0" data-id="element-2691">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center" data-id="element-2692">
                  <CreditCardIcon className="mr-2 h-6 w-6 text-green-600" data-id="element-2693" />
                  Payment Management
                </h1>
                <p className="text-gray-600 mt-1" data-id="element-2694">
                  {isSuperAdmin() ? 'View and manage all payment transactions across MDAs' : `View and manage payment transactions for ${getMdaName()}`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2" data-id="element-2695">
                <button onClick={handleRefresh} disabled={isRefreshing} className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors" data-id="element-2696">
                  {isRefreshing ? <>
                      <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-2697" />
                      Refreshing...
                    </> : <>
                      <RefreshCwIcon size={16} className="mr-2" data-id="element-2698" />
                      Refresh
                    </>}
                </button>
                <div className="relative" data-id="element-2699">
                  <button onClick={() => setShowFilterMenu(!showFilterMenu)} className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors" data-id="element-2700">
                    <DownloadIcon size={16} className="mr-2" data-id="element-2701" />
                    Export
                    <ChevronDownIcon size={16} className="ml-2" data-id="element-2702" />
                  </button>
                  {showFilterMenu && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 animate-scaleUp origin-top-right" data-id="element-2703">
                      <div className="py-1" data-id="element-2704">
                        <button onClick={() => {
                      handleExport('csv');
                      setShowFilterMenu(false);
                    }} className="block px-4 py-2 text-sm text-left w-full text-gray-700 hover:bg-gray-100" data-id="element-2705">
                          Export as CSV
                        </button>
                        <button onClick={() => {
                      handleExport('excel');
                      setShowFilterMenu(false);
                    }} className="block px-4 py-2 text-sm text-left w-full text-gray-700 hover:bg-gray-100" data-id="element-2706">
                          Export as Excel
                        </button>
                        <button onClick={() => {
                      handleExport('pdf');
                      setShowFilterMenu(false);
                    }} className="block px-4 py-2 text-sm text-left w-full text-gray-700 hover:bg-gray-100" data-id="element-2707">
                          Export as PDF
                        </button>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>
          {/* Success Message */}
          {showSuccessMessage && <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md shadow-sm animate-fadeIn" data-id="element-2708">
              <div className="flex" data-id="element-2709">
                <div className="flex-shrink-0" data-id="element-2710">
                  <CheckCircleIcon size={20} className="text-green-500" data-id="element-2711" />
                </div>
                <div className="ml-3" data-id="element-2712">
                  <p className="text-sm text-green-700" data-id="element-2713">{successMessage}</p>
                </div>
              </div>
            </div>}
          {/* Payment Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6" data-id="element-2714">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2715">
              <div className="flex items-center" data-id="element-2716">
                <div className="p-2 rounded-full bg-green-100 mr-3" data-id="element-2717">
                  <CreditCardIcon size={20} className="text-green-600" data-id="element-2718" />
                </div>
                <div data-id="element-2719">
                  <p className="text-sm text-gray-500" data-id="element-2720">Total Revenue</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2721">
                    {formatCurrency(paymentStats.totalAmount)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2722">
              <div className="flex items-center" data-id="element-2723">
                <div className="p-2 rounded-full bg-blue-100 mr-3" data-id="element-2724">
                  <DollarSignIcon size={20} className="text-blue-600" data-id="element-2725" />
                </div>
                <div data-id="element-2726">
                  <p className="text-sm text-gray-500" data-id="element-2727">Average Amount</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2728">
                    {formatCurrency(paymentStats.averageAmount)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2729">
              <div className="flex items-center" data-id="element-2730">
                <div className="p-2 rounded-full bg-purple-100 mr-3" data-id="element-2731">
                  <LayersIcon size={20} className="text-purple-600" data-id="element-2732" />
                </div>
                <div data-id="element-2733">
                  <p className="text-sm text-gray-500" data-id="element-2734">Total Payments</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2735">
                    {paymentStats.totalPayments.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2736">
              <div className="flex items-center" data-id="element-2737">
                <div className="p-2 rounded-full bg-green-100 mr-3" data-id="element-2738">
                  <CheckCircleIcon size={20} className="text-green-600" data-id="element-2739" />
                </div>
                <div data-id="element-2740">
                  <p className="text-sm text-gray-500" data-id="element-2741">Successful</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2742">
                    {paymentStats.successfulPayments.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2743">
              <div className="flex items-center" data-id="element-2744">
                <div className="p-2 rounded-full bg-yellow-100 mr-3" data-id="element-2745">
                  <ClockIcon size={20} className="text-yellow-600" data-id="element-2746" />
                </div>
                <div data-id="element-2747">
                  <p className="text-sm text-gray-500" data-id="element-2748">Pending</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2749">
                    {paymentStats.pendingPayments.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-2750">
              <div className="flex items-center" data-id="element-2751">
                <div className="p-2 rounded-full bg-red-100 mr-3" data-id="element-2752">
                  <XCircleIcon size={20} className="text-red-600" data-id="element-2753" />
                </div>
                <div data-id="element-2754">
                  <p className="text-sm text-gray-500" data-id="element-2755">Failed</p>
                  <p className="text-xl font-bold text-gray-900" data-id="element-2756">
                    {paymentStats.failedPayments.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-2757">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-2758">
              {/* Search */}
              <form onSubmit={handleSearch} className="relative w-full md:max-w-xs" data-id="element-2759">
                <input type="text" placeholder="Search by ID, reference, name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm" data-id="element-2760" />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2761">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-2762" />
                </div>
                {searchQuery && <button type="button" onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-2763">
                    <XIcon size={16} className="text-gray-400 hover:text-gray-600" data-id="element-2764" />
                  </button>}
              </form>
              {/* Filters */}
              <div className="flex flex-wrap gap-2" data-id="element-2765">
                {/* Status Filter */}
                <div className="relative" data-id="element-2766">
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm bg-white" data-id="element-2767">
                    <option value="all" data-id="element-2768">All Statuses</option>
                    <option value="successful" data-id="element-2769">Successful</option>
                    <option value="pending" data-id="element-2770">Pending</option>
                    <option value="failed" data-id="element-2771">Failed</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-2772">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2773" />
                  </div>
                </div>
                {/* Date Filter */}
                <div className="relative" data-id="element-2774">
                  <select value={filterDate} onChange={e => setFilterDate(e.target.value)} className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm bg-white" data-id="element-2775">
                    <option value="all" data-id="element-2776">All Time</option>
                    <option value="today" data-id="element-2777">Today</option>
                    <option value="week" data-id="element-2778">Last 7 Days</option>
                    <option value="month" data-id="element-2779">Last 30 Days</option>
                    <option value="quarter" data-id="element-2780">Last 90 Days</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-2781">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2782" />
                  </div>
                </div>
                {/* Items Per Page */}
                <div className="relative" data-id="element-2783">
                  <select value={itemsPerPage} onChange={e => setItemsPerPage(parseInt(e.target.value))} className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm bg-white" data-id="element-2784">
                    <option value="10" data-id="element-2785">10 per page</option>
                    <option value="25" data-id="element-2786">25 per page</option>
                    <option value="50" data-id="element-2787">50 per page</option>
                    <option value="100" data-id="element-2788">100 per page</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-2789">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2790" />
                  </div>
                </div>
              </div>
            </div>
            {/* Active Filters */}
            {(filterStatus !== 'all' || filterDate !== 'all' || searchQuery) && <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-2791">
                <span className="text-sm text-gray-500" data-id="element-2792">Active filters:</span>
                {filterStatus !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2793">
                    Status:{' '}
                    {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                    <button onClick={() => setFilterStatus('all')} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-2794">
                      <XIcon size={14} data-id="element-2795" />
                    </button>
                  </span>}
                {filterDate !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2796">
                    Date:{' '}
                    {filterDate === 'today' ? 'Today' : filterDate === 'week' ? 'Last 7 Days' : filterDate === 'month' ? 'Last 30 Days' : 'Last 90 Days'}
                    <button onClick={() => setFilterDate('all')} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-2797">
                      <XIcon size={14} data-id="element-2798" />
                    </button>
                  </span>}
                {searchQuery && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2799">
                    Search: "{searchQuery}"
                    <button onClick={() => setSearchQuery('')} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-2800">
                      <XIcon size={14} data-id="element-2801" />
                    </button>
                  </span>}
                <button onClick={() => {
              setFilterStatus('all');
              setFilterDate('all');
              setSearchQuery('');
            }} className="text-sm text-red-600 hover:text-red-800 ml-2" data-id="element-2802">
                  Clear all filters
                </button>
              </div>}
          </div>
          {/* Payments Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6" data-id="element-2803">
            <div className="overflow-x-auto" data-id="element-2804">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2805">
                <thead className="bg-gray-50" data-id="element-2806">
                  <tr data-id="element-2807">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('id')} data-id="element-2808">
                      <div className="flex items-center" data-id="element-2809">
                        Payment ID
                        {sortField === 'id' && <span className="ml-1" data-id="element-2810">
                            {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-2811" /> : <ArrowDownIcon size={14} data-id="element-2812" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('amount')} data-id="element-2813">
                      <div className="flex items-center" data-id="element-2814">
                        Amount
                        {sortField === 'amount' && <span className="ml-1" data-id="element-2815">
                            {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-2816" /> : <ArrowDownIcon size={14} data-id="element-2817" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('service')} data-id="element-2818">
                      <div className="flex items-center" data-id="element-2819">
                        Service
                        {sortField === 'service' && <span className="ml-1" data-id="element-2820">
                            {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-2821" /> : <ArrowDownIcon size={14} data-id="element-2822" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2823">
                      User
                    </th>
                    {isSuperAdmin() && <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2824">
                        MDA
                      </th>}
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('date')} data-id="element-2825">
                      <div className="flex items-center" data-id="element-2826">
                        Date
                        {sortField === 'date' && <span className="ml-1" data-id="element-2827">
                            {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-2828" /> : <ArrowDownIcon size={14} data-id="element-2829" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')} data-id="element-2830">
                      <div className="flex items-center" data-id="element-2831">
                        Status
                        {sortField === 'status' && <span className="ml-1" data-id="element-2832">
                            {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-2833" /> : <ArrowDownIcon size={14} data-id="element-2834" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2835">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2836">
                  {isRefreshing ?
                // Loading skeleton
                Array.from({
                  length: itemsPerPage
                }).map((_, index) => <tr key={index} className="animate-pulse" data-id="element-2837">
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2838">
                          <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2839"></div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2840">
                          <div className="h-4 bg-gray-200 rounded w-16" data-id="element-2841"></div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2842">
                          <div className="h-4 bg-gray-200 rounded w-32" data-id="element-2843"></div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2844">
                          <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2845"></div>
                        </td>
                        {isSuperAdmin() && <td className="px-4 py-4 whitespace-nowrap" data-id="element-2846">
                            <div className="h-4 bg-gray-200 rounded w-20" data-id="element-2847"></div>
                          </td>}
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2848">
                          <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2849"></div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2850">
                          <div className="h-4 bg-gray-200 rounded w-16" data-id="element-2851"></div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2852">
                          <div className="h-4 bg-gray-200 rounded w-16" data-id="element-2853"></div>
                        </td>
                      </tr>) : getCurrentItems().length > 0 ? getCurrentItems().map((payment, index) => <tr key={payment.id} className="hover:bg-gray-50 transition-colors animate-fadeIn" style={{
                  animationDelay: `${index * 50}ms`
                }} data-id="element-2854">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2855">
                          <div className="flex items-center" data-id="element-2856">
                            <span className="truncate max-w-[120px]" data-id="element-2857">
                              {payment.id}
                            </span>
                            <button onClick={() => {
                        navigator.clipboard.writeText(payment.id);
                        setSuccessMessage('Payment ID copied to clipboard');
                        setShowSuccessMessage(true);
                        setTimeout(() => setShowSuccessMessage(false), 2000);
                      }} className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-2858">
                              <ClipboardIcon size={14} data-id="element-2859" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2860">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-2861">
                          {payment.serviceName}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2862">
                          <div className="flex items-center" data-id="element-2863">
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium" data-id="element-2864">
                              {payment.userName.charAt(0)}
                            </div>
                            <div className="ml-3" data-id="element-2865">
                              <p className="text-sm font-medium text-gray-900" data-id="element-2866">
                                {payment.userName}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2867">
                                {payment.userEmail}
                              </p>
                            </div>
                          </div>
                        </td>
                        {isSuperAdmin() && <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-2868">
                            {payment.mdaName}
                          </td>}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2869">
                          {formatDate(payment.date)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-2870">
                          <span className={`px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full border ${getStatusBadgeClass(payment.status)}`} data-id="element-2871">
                            {getStatusIcon(payment.status)}
                            <span className="ml-1.5" data-id="element-2872">
                              {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm" data-id="element-2873">
                          <div className="flex items-center space-x-3" data-id="element-2874">
                            <button onClick={() => handleViewReceipt(payment)} className="text-green-600 hover:text-green-800 font-medium flex items-center" data-id="element-2875">
                              <ReceiptIcon size={14} className="mr-1" data-id="element-2876" />
                              Receipt
                            </button>
                            <div className="relative" data-id="element-2877">
                              <button onClick={() => {
                          // Toggle dropdown menu
                        }} className="text-gray-500 hover:text-gray-700" data-id="element-2878">
                                <MoreHorizontalIcon size={16} data-id="element-2879" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>) : <tr data-id="element-2880">
                      <td colSpan={isSuperAdmin() ? 8 : 7} className="px-4 py-8 text-center" data-id="element-2881">
                        <div className="flex flex-col items-center" data-id="element-2882">
                          <SearchIcon size={40} className="text-gray-400 mb-3" data-id="element-2883" />
                          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2884">
                            No payments found
                          </h3>
                          <p className="text-gray-500 mb-4" data-id="element-2885">
                            {searchQuery ? `No payments matching "${searchQuery}" were found.` : 'No payments match the current filter criteria.'}
                          </p>
                          <button onClick={() => {
                        setFilterStatus('all');
                        setFilterDate('all');
                        setSearchQuery('');
                      }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-2886">
                            Clear Filters
                          </button>
                        </div>
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {filteredPayments.length > 0 && <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-2887">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-2888">
                  <div data-id="element-2889">
                    <p className="text-sm text-gray-700" data-id="element-2890">
                      Showing{' '}
                      <span className="font-medium" data-id="element-2891">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-2892">
                        {Math.min(currentPage * itemsPerPage, totalItems)}
                      </span>{' '}
                      of <span className="font-medium" data-id="element-2893">{totalItems}</span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-2894">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-2895">
                      <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-2896">
                        <span className="sr-only" data-id="element-2897">Previous</span>
                        <ChevronLeftIcon size={16} aria-hidden="true" data-id="element-2898" />
                      </button>
                      {/* Page numbers */}
                      {Array.from({
                    length: Math.ceil(totalItems / itemsPerPage)
                  }, (_, i) => i + 1).filter(page => page === 1 || page === Math.ceil(totalItems / itemsPerPage) || Math.abs(page - currentPage) <= 1).map((page, i, array) => {
                    // Add ellipsis
                    if (i > 0 && array[i - 1] !== page - 1) {
                      return <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700" data-id="element-2899">
                                ...
                              </span>;
                    }
                    return <button key={page} onClick={() => handlePageChange(page)} className={`relative inline-flex items-center px-4 py-2 border ${page === currentPage ? 'z-10 bg-green-50 border-green-500 text-green-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-2900">
                              {page}
                            </button>;
                  })}
                      <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === Math.ceil(totalItems / itemsPerPage)} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === Math.ceil(totalItems / itemsPerPage) ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-2901">
                        <span className="sr-only" data-id="element-2902">Next</span>
                        <ChevronRightIcon size={16} aria-hidden="true" data-id="element-2903" />
                      </button>
                    </nav>
                  </div>
                </div>
                {/* Mobile pagination */}
                <div className="flex items-center justify-between w-full sm:hidden" data-id="element-2904">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'text-gray-300 bg-gray-50 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-2905">
                    Previous
                  </button>
                  <p className="text-sm text-gray-700" data-id="element-2906">
                    Page <span className="font-medium" data-id="element-2907">{currentPage}</span> of{' '}
                    <span className="font-medium" data-id="element-2908">
                      {Math.ceil(totalItems / itemsPerPage)}
                    </span>
                  </p>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === Math.ceil(totalItems / itemsPerPage)} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === Math.ceil(totalItems / itemsPerPage) ? 'text-gray-300 bg-gray-50 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-2909">
                    Next
                  </button>
                </div>
              </div>}
          </div>
        </main>
        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 p-4 text-center text-sm text-gray-600" data-id="element-2910">
          <p data-id="element-2911">Edo State Government Admin Portal © 2025</p>
          <p className="mt-1" data-id="element-2912">Powered by Edo ICT Agency</p>
        </footer>
      </div>
      {/* Receipt Modal */}
      {showReceiptModal && selectedPayment && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2913">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleUp" data-id="element-2914">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center" data-id="element-2915">
              <h2 className="text-xl font-bold text-gray-900 flex items-center" data-id="element-2916">
                <ReceiptIcon size={20} className="mr-2 text-green-600" data-id="element-2917" />
                Payment Receipt
              </h2>
              <button onClick={() => setShowReceiptModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-2918">
                <XIcon size={20} data-id="element-2919" />
              </button>
            </div>
            <div className="p-6" data-id="element-2920">
              <div className="flex justify-between items-start mb-6" data-id="element-2921">
                <div data-id="element-2922">
                  <div className="flex items-center" data-id="element-2923">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-12 w-12 mr-3" data-id="element-2924" />
                    <div data-id="element-2925">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-2926">
                        Edo State Government
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-2927">
                        Official Payment Receipt
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-2928">
                  <p className="text-sm text-gray-600" data-id="element-2929">Receipt No:</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2930">
                    {selectedPayment.referenceNumber}
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-2931">Date:</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2932">
                    {formatDate(selectedPayment.date)}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-2933">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2934">
                  PAYMENT DETAILS
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2935">
                  <div data-id="element-2936">
                    <p className="text-sm text-gray-600" data-id="element-2937">Payment ID:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2938">
                      {selectedPayment.id}
                    </p>
                  </div>
                  <div data-id="element-2939">
                    <p className="text-sm text-gray-600" data-id="element-2940">Transaction ID:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2941">
                      {selectedPayment.transactionId}
                    </p>
                  </div>
                  <div data-id="element-2942">
                    <p className="text-sm text-gray-600" data-id="element-2943">Status:</p>
                    <div className="mt-1" data-id="element-2944">
                      <span className={`px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full border ${getStatusBadgeClass(selectedPayment.status)}`} data-id="element-2945">
                        {getStatusIcon(selectedPayment.status)}
                        <span className="ml-1.5" data-id="element-2946">
                          {selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div data-id="element-2947">
                    <p className="text-sm text-gray-600" data-id="element-2948">Payment Method:</p>
                    <p className="text-sm font-medium text-gray-900 capitalize" data-id="element-2949">
                      {selectedPayment.paymentMethod}
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-2950">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2951">
                  SERVICE INFORMATION
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2952">
                  <div data-id="element-2953">
                    <p className="text-sm text-gray-600" data-id="element-2954">Service:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2955">
                      {selectedPayment.serviceName}
                    </p>
                  </div>
                  <div data-id="element-2956">
                    <p className="text-sm text-gray-600" data-id="element-2957">MDA:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2958">
                      {selectedPayment.mdaName}
                    </p>
                  </div>
                  <div data-id="element-2959">
                    <p className="text-sm text-gray-600" data-id="element-2960">Application ID:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2961">
                      {selectedPayment.applicationId}
                    </p>
                  </div>
                  <div data-id="element-2962">
                    <p className="text-sm text-gray-600" data-id="element-2963">Description:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2964">
                      {selectedPayment.description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-2965">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2966">
                  PAYER INFORMATION
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2967">
                  <div data-id="element-2968">
                    <p className="text-sm text-gray-600" data-id="element-2969">Name:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2970">
                      {selectedPayment.userName}
                    </p>
                  </div>
                  <div data-id="element-2971">
                    <p className="text-sm text-gray-600" data-id="element-2972">Email:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2973">
                      {selectedPayment.userEmail}
                    </p>
                  </div>
                  <div data-id="element-2974">
                    <p className="text-sm text-gray-600" data-id="element-2975">Phone:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2976">
                      {selectedPayment.userPhone}
                    </p>
                  </div>
                  <div data-id="element-2977">
                    <p className="text-sm text-gray-600" data-id="element-2978">User ID:</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2979">
                      {selectedPayment.userId}
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6 mb-6" data-id="element-2980">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2981">
                  PAYMENT BREAKDOWN
                </h4>
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-2982">
                  <div className="flex justify-between py-2" data-id="element-2983">
                    <span className="text-sm text-gray-600" data-id="element-2984">Service Fee:</span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-2985">
                      {formatCurrency(selectedPayment.amount - selectedPayment.processingFee)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-2986">
                    <span className="text-sm text-gray-600" data-id="element-2987">
                      Processing Fee:
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-2988">
                      {formatCurrency(selectedPayment.processingFee)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 font-bold" data-id="element-2989">
                    <span className="text-sm text-gray-800" data-id="element-2990">Total Amount:</span>
                    <span className="text-sm text-gray-900" data-id="element-2991">
                      {formatCurrency(selectedPayment.amount)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6" data-id="element-2992">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-md mb-6" data-id="element-2993">
                  <div className="flex" data-id="element-2994">
                    <div className="flex-shrink-0" data-id="element-2995">
                      <InfoIcon size={20} className="text-green-500" data-id="element-2996" />
                    </div>
                    <div className="ml-3" data-id="element-2997">
                      <p className="text-sm text-green-700" data-id="element-2998">
                        This is an official receipt. For any inquiries, please
                        contact the {selectedPayment.mdaName} or the Edo State
                        Internal Revenue Service.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2" data-id="element-2999">
                  <button onClick={handleDownloadReceipt} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3000">
                    <DownloadIcon size={16} className="mr-2" data-id="element-3001" />
                    Download
                  </button>
                  <button onClick={handlePrintReceipt} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3002">
                    <PrinterIcon size={16} className="mr-2" data-id="element-3003" />
                    Print
                  </button>
                  <button onClick={() => setShowReceiptModal(false)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3004">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default PaymentManagement;